import firebase from 'firebase';
import { v4 as uuidv4 } from 'uuid';
import { MaintenanceFormStatus } from '@/enums/MaintenanceFormStatus';
import Loader from '@/components/loader/Loader.vue';
import { fileSizeToMB, validFileSize10MB, validVideoSize130MB } from '@/utils/fileSizeToMB';
import { waitResizedImage } from '@/utils/wait-resized-image';
import { MaintenanceMediaTypes } from '@/enums/MaintenanceMediaTypes';
import { MaintenanceMedia } from '@/models/maintenance/MaintenanceMedia';
import { deleteFileByUrl, getBase64SizeInMB, optimizePhotos, uploadFileByBase64, uploadImageAsync } from '@/utils/upload-image';

export default {
  name: 'scheduledMaintenanceFormCompleteDlg',
  components: {
    Loader
  },
  data() {
    return {
      dialog: false,
      template: null,
      loading: true,
      maintenanceFormModel: null,
      mediaLoading: false
    }
  },
  computed: {
    form() {
      return JSON.parse(this.$store.state.maintenanceModule.maintenanceForm.template);
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    maintenanceForm() {
      return this.$store.state.maintenanceModule.maintenanceForm;
    },
    isFormCompleted() {
      return this.$store.state.maintenanceModule.maintenanceForm?.status === MaintenanceFormStatus.Completed;
    },
    MaintenanceMediaTypes() {
      return MaintenanceMediaTypes;
    }
  },
  methods: {
    async open(form) {
      this.dialog = true;

      await this.$store.dispatch("getMaintenanceForm", form.formId)
        .then(() => {
          this.template = this.form;
          this.loading = false;
          this.createMaintenanceForm()
        });
    },
    close() {
      this.$store.dispatch('getMaintenanceFormsByMaintenanceRequestId', this.$route.params.id);
      this.dialog = false;
    },
    async createMaintenanceForm() {
      const maintenanceForm = this.maintenanceForm;
      const maintenanceResult = this.maintenanceForm?.result;

      if (maintenanceForm) {
        const SurveyKnockout = require("survey-knockout-ui");
        this.maintenanceFormModel = new SurveyKnockout.Model(this.template)
        this.maintenanceFormModel.completedHtml = "<h3>Thank you for completing the form</h3>";

        if(this.isFormCompleted) {
          //this.maintenanceFormModel.showNavigationButtons = false;
          this.maintenanceFormModel.mode = "display";
        }

        if (maintenanceResult && Object.keys(maintenanceResult).length) {
          this.maintenanceFormModel.data = maintenanceResult;
        }

        const saveForm = this.saveMaintenanceFormResult;

        this.maintenanceFormModel.onValueChanging.add((result, options) => {
          const isCanvasCleared = (typeof(options.value) === 'undefined') && typeof(options.oldValue) === 'string' && options.oldValue?.includes('data:image/png;base64') && options.question?.signaturePad?.isEmpty();
          if(isCanvasCleared) {
            options.value = "";
          }

          const isFileCleared = (typeof(options.value) === 'undefined') && options.oldValue?.hasOwnProperty('content') && (options.oldValue.type.startsWith('image') || options.oldValue.type.startsWith('video'))
          if(isFileCleared){
            options.value = []
          }

          if(typeof(options.value) === 'undefined' && (!isFileCleared ?? !isCanvasCleared)) return

          if(Array.isArray(options.value) && options.value[0]?.hasOwnProperty('content') && options.value[0].content?.includes('base64')) {
            const sizeMB = getBase64SizeInMB(options.value[0].content)

            if(sizeMB <= 133){
              this.errorMessage = null
              saveForm(options.name, options.value)
            } else{
              this.errorMessage = `The selected file is too large. Maximum allowed size is 133 MB. Your file is ${sizeMB.toFixed(2)} MB.`
            }
          } else {
            saveForm(options.name, options.value)
          }
        })

        // SurveyJS doesn't fire onValueChanging for comments (e.g. "Other" text), only onValueChanged
        this.maintenanceFormModel.onValueChanged.add((result, options) => {
          const commentSuffix = this.maintenanceFormModel.commentSuffix;
          if (options.name?.endsWith(commentSuffix)) {
            saveForm(options.name, options.value || null);
          }
        });

        this.maintenanceFormModel.onComplete.add(async () => {
          await this.completeFormForRequest();

          this.$toast.open({
            message: this.$t("MaintenanceView.FormCompleted"),
            type: "success"
          });
        });

        this.maintenanceFormModel.render('surveyContainer');

        this.maintenanceForm = this.maintenanceFormModel;
      }
    },
    async saveMaintenanceFormResult(name, value) {
      this.maintenanceFormModel.allowComplete = false;

      if(Array.isArray(value) && value[0]?.hasOwnProperty('content') && value[0].content?.includes('base64')) {
        this.maintenanceForm.result[name] = await this.uploadResultMedia(value[0], this.maintenanceForm.facilityId)
        this.maintenanceForm.result[name].storeDataAsText = false
      } else {
        this.maintenanceForm.result[name] = value
      }

      await firebase
        .firestore()
        .collection("maintenanceForms")
        .doc(this.maintenanceForm.id)
        .update({ result: this.maintenanceForm.result })
        .then()
        .finally(()=> {
          this.maintenanceFormModel.allowComplete = true;
        });
    },
    async uploadResultMedia(fileData: any, facilityId: string) {
      const maintenanceFormId = this.maintenanceForm.id;

      if (fileData.content && fileData.content?.includes('base64')) {
        const base64 = fileData.content;

        if (fileData.type?.startsWith("image/") || fileData.type?.startsWith("video/")) {
          const fileExt = fileData.type.split("/")[1];
          const fileName = `${uuidv4()}.${fileExt}`;
          const path = `${facilityId}/maintenanceForms/${maintenanceFormId}/${fileName}`;

          const uploadedFileUrl = await uploadFileByBase64({
            path,
            base64: base64,
            onStart: () => {
              console.log(`start uploading ${fileData.type} file`);
            },
            onError: (error) => {
              console.log(`error uploading ${fileData.type} file:`, error);
            }
          });

          if (uploadedFileUrl) {
            this.uploadedFile = { name: fileName, url: uploadedFileUrl, path: path, fileExt: fileExt}
            console.log(`file ${fileData.type} uploaded. url:`, uploadedFileUrl);

            fileData.name = fileName
            fileData.content = uploadedFileUrl

            return fileData
          }
        }
      }

      return null;
    },
    async uploadMedia(e) {
      this.mediaLoading = true;
      const files = e.target.files;

      if (files.length) {
        const promises = [];

        for (let i = 0; i < files.length; i++) {
          const path = `${this.facility.id}/maintenanceForms/staff/${uuidv4()}`;
          const isFileVideo = ["video/mp4", "video/webm"].includes(files[i].type);
          const file = isFileVideo ? files[i] : await optimizePhotos(files[i], 'background-image');
          const fileSizeInMegabytes = fileSizeToMB(file?.size);
          const isValidFileSize = isFileVideo ? validVideoSize130MB(fileSizeInMegabytes) : validFileSize10MB(fileSizeInMegabytes);

          if (!isValidFileSize) {
            this.$refs.inputUploadMedia.value = null;
            this.mediaLoading = false;
            return;
          }

          const promise = uploadImageAsync({ path, file })
            .then(async (storage) => {
              const url = await storage.ref.getDownloadURL();
              let thumbnailUrl = null;

              if(!isFileVideo) {
                const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);
                thumbnailUrl = await waitResizedImage(10, resizedImageRef);
              }

              const thumbnail = isFileVideo ? url : thumbnailUrl;
              const type = isFileVideo ? MaintenanceMediaTypes.Video : MaintenanceMediaTypes.Photo;
              this.maintenanceForm.media.push(new MaintenanceMedia(url, thumbnail, type).toObject());

              return true;
            });

          promises.push(promise);
        }

        try {
          await Promise.all(promises).then(async () => {
            await this.updateFormMedia()

            this.$toast.open({
              message: files.length > 1 ? this.$t('Gallery.SuccessUploadingImages') : this.$t('Gallery.SuccessUploadingImage'),
              type: "success"
            });

            this.clearInputFile();
          })
        } catch (error) {
          console.error("Error uploading images:", error);
        } finally {
          this.mediaLoading = false;
        }
      }
    },
    clearInputFile() {
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      fileInput.value = "";
    },
    async deleteMedia(index: number) {
      const media = this.maintenanceForm.media[index];
      const mediaUrl = media?.url;
      const mediaThumbnail = media?.thumbnailUrl;

      await deleteFileByUrl(mediaUrl);
      if(mediaUrl !== mediaThumbnail) {
        await deleteFileByUrl(mediaThumbnail);
      }

      this.maintenanceForm.media.splice(index, 1);
      await this.updateFormMedia();
    },
    async completeFormForRequest() {
      try {
        await firebase
          .firestore()
          .collection("maintenanceForms")
          .doc(this.maintenanceForm.id)
          .update({
            status: MaintenanceFormStatus.Completed
          });

        await firebase
          .firestore()
          .collection("maintenanceRequests")
          .doc(this.$route.params.id)
          .get()
          .then(async doc => {
            const docDoc = doc.data()
            const forms = docDoc.forms ?? []

            forms.forEach(form => {
              if(form.formId === this.maintenanceForm.id) {
                form.status = MaintenanceFormStatus.Completed
              }
            })

            await doc.ref.update({
              forms: forms
            })
          })

        await this.$store.dispatch('getMaintenanceRequest', this.$route.params.id);
      } catch (err) {
        this.$toast.open({
          message: this.$t("GeneralTranslations.UnhandledError"),
          type: "error"
        });
        console.log("ERROR ", err);
      } finally {
        this.close();
      }
    },
    async updateFormMedia() {
      try {
        await firebase
          .firestore()
          .collection("maintenanceForms")
          .doc(this.maintenanceForm.id)
          .update({
            media: this.maintenanceForm.media
          });
      } catch (err) {
        this.$toast.open({
          message: this.$t("GeneralTranslations.UnhandledError"),
          type: "error"
        });
        console.log("ERROR ", err);
      }
    }
  }
};